"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DashboardLayoutWidget = void 0;
var tslib_1 = require("tslib");
// (C) 2020-2023 GoodData Corporation
var sdk_model_1 = require("@gooddata/sdk-model");
var sdk_ui_ext_1 = require("@gooddata/sdk-ui-ext");
var react_1 = tslib_1.__importStar(require("react"));
var classnames_1 = tslib_1.__importDefault(require("classnames"));
var model_1 = require("../../model");
var widgets_1 = require("../../widgets");
var sizing_1 = require("../../_staging/layout/sizing");
var coordinates_1 = require("../../_staging/layout/coordinates");
var dashboardContexts_1 = require("../dashboardContexts");
var dragAndDrop_1 = require("../dragAndDrop");
var widget_1 = require("../widget");
var constants_1 = require("./constants");
var DefaultDashboardLayoutRenderer_1 = require("./DefaultDashboardLayoutRenderer");
var DashboardItemOverlay_1 = require("./DashboardItemOverlay/DashboardItemOverlay");
var refs_1 = require("./refs");
/**
 * Tests in KD require widget index for css selectors.
 * Widget index equals to the widget order in the layout.
 * Also placeholders are ignored for this.
 */
function getWidgetIndex(item) {
    var _a, _b;
    var sectionIndex = item.section().index();
    var isIgnoredForIndexes = function (widget) {
        return !widget || widgets_1.isAnyPlaceholderWidget(widget);
    };
    var itemsInSectionsBefore = 0;
    for (var i = 0; i < sectionIndex; i += 1) {
        itemsInSectionsBefore += (_b = (_a = item
            .section()
            .layout()
            .section(i)) === null || _a === void 0 ? void 0 : _a.items().filter(function (i) { return !isIgnoredForIndexes(i.widget()); }).length) !== null && _b !== void 0 ? _b : 0;
    }
    var ignoredWidgetsBeforeItemCount = item
        .section()
        .items()
        .filter(function (i) { return i.index() < item.index() && isIgnoredForIndexes(i.widget()); }).length;
    return itemsInSectionsBefore + item.index() - ignoredWidgetsBeforeItemCount;
}
/**
 * @internal
 */
exports.DashboardLayoutWidget = function (props) {
    var item = props.item, screen = props.screen, DefaultWidgetRenderer = props.DefaultWidgetRenderer, onDrill = props.onDrill, onFiltersChange = props.onFiltersChange, onError = props.onError, getLayoutDimensions = props.getLayoutDimensions;
    var dispatch = model_1.useDashboardDispatch();
    var insights = model_1.useDashboardSelector(model_1.selectInsightsMap);
    var settings = model_1.useDashboardSelector(model_1.selectSettings);
    var isInEditMode = model_1.useDashboardSelector(model_1.selectIsInEditMode);
    var isExport = model_1.useDashboardSelector(model_1.selectIsExport);
    var enableWidgetCustomHeight = model_1.useDashboardSelector(model_1.selectEnableWidgetCustomHeight);
    var handleDragEnd = dragAndDrop_1.useWidgetDragEndHandler();
    // TODO: we should probably do something more meaningful when item has no widget; should that even
    //  be allowed? undefined widget will make things explode down the line away so..
    var widget = item.widget();
    var isCustom = model_1.isCustomWidget(widget);
    var isDraggableWidgetType = !(widgets_1.isPlaceholderWidget(widget) || isCustom);
    var _a = tslib_1.__read(dragAndDrop_1.useDashboardDrag({
        dragItem: function () {
            return createDraggableItem(item, insights, settings);
        },
        canDrag: isInEditMode && isDraggableWidgetType,
        dragStart: function (item) {
            dispatch(model_1.uiActions.setDraggingWidgetSource(item));
        },
        dragEnd: handleDragEnd,
    }, [item, insights, isInEditMode]), 2), isDragging = _a[0].isDragging, dragRef = _a[1];
    var _b = dashboardContexts_1.useDashboardComponentsContext(), ErrorComponent = _b.ErrorComponent, LoadingComponent = _b.LoadingComponent;
    var currentSize = item.size()[screen];
    var minHeight = sizing_1.calculateWidgetMinHeight(widget, currentSize, insights, settings);
    var height = currentSize.heightAsRatio && !currentSize.gridHeight
        ? DefaultDashboardLayoutRenderer_1.getDashboardLayoutItemHeightForRatioAndScreen(currentSize, screen)
        : undefined;
    var allowOverflow = !!currentSize.heightAsRatio;
    var className = enableWidgetCustomHeight ? "custom-height" : undefined;
    var index = getWidgetIndex(item);
    var refs = refs_1.getRefsForItem(item);
    var sectionModifications = model_1.useDashboardSelector(model_1.selectSectionModification(refs_1.getRefsForSection(item.section())));
    var itemModifications = model_1.useDashboardSelector(model_1.selectWidgetsModification(refs));
    var overlayShow = model_1.useDashboardSelector(model_1.selectWidgetsOverlayState(refs));
    var _c = dragAndDrop_1.useResizeItemStatus(widget.identifier), isActive = _c.isActive, isResizingColumnOrRow = _c.isResizingColumnOrRow, heightLimitReached = _c.heightLimitReached, widthLimitReached = _c.widthLimitReached;
    var contentRef = react_1.useRef(null);
    function getWidthInPx() {
        return (contentRef === null || contentRef === void 0 ? void 0 : contentRef.current) ? contentRef.current.getBoundingClientRect().width
            : constants_1.DEFAULT_COLUMN_CLIENT_WIDTH;
    }
    function getHeightInPx() {
        return (contentRef === null || contentRef === void 0 ? void 0 : contentRef.current) ? contentRef.current.getBoundingClientRect().height
            : constants_1.DEFAULT_WIDTH_RESIZER_HEIGHT;
    }
    function getGridColumnWidth() {
        var _a;
        var columnWidthInGC = (_a = item.sizeForScreen(screen)) === null || _a === void 0 ? void 0 : _a.gridWidth;
        var columnWidthInPx = getWidthInPx();
        return columnWidthInPx / columnWidthInGC;
    }
    var canShowHotspot = isInEditMode && !isDragging;
    return (react_1.default.createElement(DefaultWidgetRenderer, { DefaultWidgetRenderer: DefaultWidgetRenderer, item: item, screen: screen, allowOverflow: allowOverflow, height: height, minHeight: minHeight, className: className, contentRef: contentRef, getLayoutDimensions: getLayoutDimensions },
        react_1.default.createElement("div", { ref: dragRef, className: classnames_1.default([
                "dashboard-widget-draggable-wrapper",
                { "gd-custom-widget-export": isCustom && isExport },
            ]) },
            react_1.default.createElement(widget_1.DashboardWidget
            // @ts-expect-error Don't expose index prop on public interface (we need it only for css class for KD tests)
            , { 
                // @ts-expect-error Don't expose index prop on public interface (we need it only for css class for KD tests)
                index: index, screen: screen, onDrill: onDrill, onError: onError, onFiltersChange: onFiltersChange, widget: widget, ErrorComponent: ErrorComponent, LoadingComponent: LoadingComponent })),
        canShowHotspot && !widgets_1.isAnyPlaceholderWidget(widget) ? (react_1.default.createElement(react_1.default.Fragment, null,
            react_1.default.createElement(dragAndDrop_1.ResizeOverlay, { isActive: isActive, isResizingColumnOrRow: isResizingColumnOrRow, reachedWidthLimit: widthLimitReached, reachedHeightLimit: heightLimitReached }),
            !model_1.isCustomWidget(widget) ? (react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement(dragAndDrop_1.Hotspot, { dropZoneType: "prev", itemIndex: item.index(), sectionIndex: item.section().index(), isLastInSection: false }),
                react_1.default.createElement(dragAndDrop_1.Hotspot, { dropZoneType: "next", itemIndex: item.index(), sectionIndex: item.section().index(), isLastInSection: item.isLast() }),
                react_1.default.createElement(dragAndDrop_1.WidthResizerHotspot, { item: item, screen: screen, getGridColumnHeightInPx: getHeightInPx, getGridColumnWidth: getGridColumnWidth, getLayoutDimensions: getLayoutDimensions }))) : null)) : null,
        react_1.default.createElement(DashboardItemOverlay_1.DashboardItemOverlay, { type: "column", onHide: function () {
                return dispatch(model_1.uiActions.toggleWidgetsOverlay({
                    refs: [item.ref()],
                    visible: false,
                }));
            }, render: Boolean(isInEditMode && overlayShow) && !sectionModifications.includes("insertedByPlugin"), modifications: itemModifications })));
};
function getFilledSize(itemSize, sizeInfo) {
    var _a, _b;
    return {
        gridWidth: ((_a = itemSize.xl) === null || _a === void 0 ? void 0 : _a.gridWidth) || (sizeInfo === null || sizeInfo === void 0 ? void 0 : sizeInfo.width.default) ||
            sdk_ui_ext_1.WIDGET_DROPZONE_SIZE_INFO_DEFAULT.width.default,
        gridHeight: ((_b = itemSize.xl) === null || _b === void 0 ? void 0 : _b.gridHeight) || (sizeInfo === null || sizeInfo === void 0 ? void 0 : sizeInfo.height.default) ||
            sdk_ui_ext_1.WIDGET_DROPZONE_SIZE_INFO_DEFAULT.height.default,
    };
}
function createDraggableItem(item, insights, settings) {
    var widget = item.widget();
    var _a = coordinates_1.getLayoutCoordinates(item), sectionIndex = _a.sectionIndex, itemIndex = _a.itemIndex;
    var size = item.size();
    var isOnlyItemInSection = item.section().items().count() === 1;
    if (sdk_model_1.isKpiWidget(widget)) {
        var sizeInfo = sizing_1.getSizeInfo(settings, "kpi", widget.kpi);
        return {
            type: "kpi",
            kpi: widget.kpi,
            title: widget.title,
            sectionIndex: sectionIndex,
            itemIndex: itemIndex,
            isOnlyItemInSection: isOnlyItemInSection,
            size: getFilledSize(size, sizeInfo),
        };
    }
    else if (sdk_model_1.isInsightWidget(widget)) {
        var insight = insights.get(widget.insight);
        var sizeInfo = sizing_1.getSizeInfo(settings, "kpi", insight);
        return {
            type: "insight",
            insight: insight,
            sectionIndex: sectionIndex,
            itemIndex: itemIndex,
            title: widget.title,
            isOnlyItemInSection: isOnlyItemInSection,
            size: getFilledSize(size, sizeInfo),
        };
    }
    else {
        return {
            type: widget.type,
            widget: widget,
            sectionIndex: sectionIndex,
            itemIndex: itemIndex,
            title: "",
            isOnlyItemInSection: isOnlyItemInSection,
            size: getFilledSize(size),
        };
    }
}
//# sourceMappingURL=DashboardLayoutWidget.js.map